import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  ExcelTableColDef,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import FormSection from 'components/ui/form-section';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimTabProps } from '../claim-registration';
import { useSearchParams, useNavigate } from 'react-router';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import {
  binderClaimApi,
  claimPaymentsApi,
  referenceDataApi,
} from 'services/apis';
import usePagination from 'hooks/usePagination';
import dayjs from 'dayjs';
import { Form, DatePicker, Input, Space } from 'antd';
import FilterButton from 'components/ui/filter-button';
import { SortedSelect } from 'components/ui/sorted-select';
import SearchIcon from 'assets/icons/search';
import {
  FilterBinderClaimPaymentHistoryQueryDtoTransactionTypesEnum,
  FilterBinderClaimPaymentHistoryQueryDtoPaymentStatusesEnum,
} from 'libs/pinnacle-sdk';
import { ENavigationPath } from 'constants/path';
import { RowClickedEvent } from 'ag-grid-community';

const { RangePicker } = DatePicker;

type ClaimsHistoryDisplayedData = {
  id: number;
  claimPaymentId: string;
  claimId: string;
  contractId: string;
  transactionDate: string;
  transactionType: string;
  transactionNumber: string;
  fromPinnacleBankAccount: string;
  currency: string;
  thisPaymentOurShare: string;
  outstandingOurShare: string;
  paymentStatus: string;
};

const ClaimsHistory: React.FC<ClaimTabProps> = ({ claim }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const claimNo = searchParams.get('claimNo');

  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(20);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [inputText, setInputText] = useState('');

  // Fetch binder claim data to get claimId and contractId (only when claimNo is available)
  const { data: binderClaim } = useQuery(
    [EQueryKeys.GET_BINDER_CLAIM_BY_ID, claimNo],
    async () => {
      return binderClaimApi.getBinderClaimById({
        claimNo: claimNo as string,
        includeInsured: false,
        includeReinsured: false,
        includeBroker: false,
        includeClaimHandlerUser: false,
      });
    },
    { enabled: !!claimNo }
  );

  // Use binder claim's claimId/contractId if available, otherwise use master claim's claimId
  const claimId = binderClaim?.claimId || claim.claimId;
  const contractId = binderClaim?.contractId;

  // Watch form values for filtering
  const dateRange = Form.useWatch('dateRange', form);
  const selectedTransactionType = Form.useWatch('transactionType', form);
  const selectedCurrency = Form.useWatch('currency', form);
  const selectedPaymentStatus = Form.useWatch('paymentStatus', form);
  const selectedBankAccount = Form.useWatch('bankAccount', form);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [
    dateRange,
    selectedTransactionType,
    selectedCurrency,
    selectedPaymentStatus,
    selectedBankAccount,
    resetPagination,
  ]);

  // Transaction type options from enum
  const transactionTypeOptions = useMemo(
    () =>
      Object.values(
        FilterBinderClaimPaymentHistoryQueryDtoTransactionTypesEnum
      ).map((type) => ({
        label: type,
        value: type,
      })),
    []
  );

  // Payment status options from enum
  const paymentStatusOptions = useMemo(
    () =>
      Object.values(
        FilterBinderClaimPaymentHistoryQueryDtoPaymentStatusesEnum
      ).map((status) => ({
        label: status,
        value: status,
      })),
    []
  );

  // Fetch currencies
  const { data: currencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => referenceDataApi.getCurrencies(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  // Currency options for filter
  const currencyOptions = useMemo(
    () =>
      (currencies || []).map((currency) => ({
        label: `${currency.name} (${currency.code})`,
        value: currency.code,
      })),
    [currencies]
  );

  // Fetch bank accounts
  const { data: bankAccounts } = useQuery(
    EQueryKeys.LIST_BANK_ACCOUNTS,
    async () => referenceDataApi.getBankAccounts(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  // Bank account options for filter
  const bankAccountOptions = useMemo(
    () =>
      (bankAccounts || []).map((account) => ({
        label: account.accountName,
        value: account.accountNumber,
      })),
    [bankAccounts]
  );

  // Fetch payment history data
  const {
    data: { items: paymentHistoryData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading,
  } = useQuery(
    [
      EQueryKeys.BINDER_CLAIM_PAYMENT_HISTORY,
      claimId,
      contractId,
      currentPage,
      pageSize,
      dateRange,
      selectedTransactionType,
      selectedCurrency,
      selectedPaymentStatus,
      selectedBankAccount,
    ],
    async () => {
      const result = await claimPaymentsApi.filterBinderClaimPaymentHistory({
        claimId: claimId as string,
        page: currentPage,
        pageSize,
        filterBinderClaimPaymentHistoryQueryDto: {
          contractId,
          transactionDateFrom: dateRange?.[0]?.startOf('day').toDate(),
          transactionDateTo: dateRange?.[1]?.endOf('day').toDate(),
          transactionTypes: selectedTransactionType?.length
            ? selectedTransactionType
            : undefined,
          currencies: selectedCurrency?.length ? selectedCurrency : undefined,
          paymentStatuses: selectedPaymentStatus?.length
            ? selectedPaymentStatus
            : undefined,
          bankAccountNumbers: selectedBankAccount?.length
            ? selectedBankAccount
            : undefined,
        },
      });
      return result;
    },
    {
      enabled: !!claimId,
    }
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'transactionDate',
        headerName: t('claim.claimsHistory.transactionDate'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'transactionType',
        headerName: t('claim.claimsHistory.transactionType'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'transactionNumber',
        headerName: t('claim.claimsHistory.transactionNumber'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'fromPinnacleBankAccount',
        headerName: t('claim.claimsHistory.fromPinnacleBankAccount'),
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'currency',
        headerName: t('claim.claimsHistory.currency'),
        minWidth: 120,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'thisPaymentOurShare',
        headerName: t('claim.claimsHistory.thisPaymentOurShare'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'outstandingOurShare',
        headerName: t('claim.claimsHistory.outstandingOurShare'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'paymentStatus',
        headerName: t('claim.claimsHistory.paymentStatus'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
    ],
    [t]
  );

  // Map API data to row format
  const rowData: ClaimsHistoryDisplayedData[] = useMemo(() => {
    return paymentHistoryData.map((payment, index) => ({
      id: pageSize * (currentPage - 1) + index + 1,
      claimPaymentId: payment.claimPaymentId,
      claimId: payment.claimId,
      contractId: payment.contractId,
      transactionDate: dayjs(payment.transactionDate).format('DD/MM/YYYY'),
      transactionType: payment.transactionType,
      transactionNumber: payment.transactionId,
      fromPinnacleBankAccount: payment.bankAccountNumber,
      currency: payment.settlementCurrency,
      thisPaymentOurShare: payment.thisPaymentOurShare,
      outstandingOurShare: payment.outstandingOurShare,
      paymentStatus: payment.paymentStatus,
    }));
  }, [paymentHistoryData, currentPage, pageSize]);

  // Handle row click to navigate to binder claims actions
  const handleRowClick = useCallback(
    (row: RowClickedEvent<ClaimsHistoryDisplayedData>) => {
      if (row.data && claimNo) {
        navigate(
          ENavigationPath.CLAIM_DETAIL.replace(
            ':claimId',
            claim.claimId
          ).replace(':tabId', '4') + `?claimNo=${claimNo}`
        );
      }
    },
    [navigate, claim.claimId, claimNo]
  );

  return (
    <div>
      <FormSection title={t('claim.claimsHistory.title')} collapsible>
        <Form form={form} className="w-full">
          <div className="flex items-center justify-end mb-4">
            <FilterButton
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
          </div>

          {isFilterOpen && (
            <Space direction="horizontal" wrap className="mb-4">
              <Form.Item name="dateRange" className="w-[280px] mb-0">
                <RangePicker
                  format="DD/MM/YYYY"
                  size="large"
                  placeholder={[t('general.startDate'), t('general.endDate')]}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item name="transactionType" className="min-w-[180px] mb-0">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('claim.claimsHistory.transactionType')}
                  options={transactionTypeOptions}
                  size="large"
                  maxTagCount={1}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="currency" className="min-w-[150px] mb-0">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('claim.claimsHistory.currency')}
                  options={currencyOptions}
                  size="large"
                  maxTagCount={1}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="paymentStatus" className="min-w-[180px] mb-0">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('claim.claimsHistory.paymentStatus')}
                  options={paymentStatusOptions}
                  size="large"
                  maxTagCount={1}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="bankAccount" className="min-w-[250px] mb-0">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('claim.claimsHistory.fromPinnacleBankAccount')}
                  options={bankAccountOptions}
                  size="large"
                  maxTagCount={1}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Space>
          )}
        </Form>

        <ExcelTable
          data={rowData}
          columnDefs={columnDefs}
          paginationType={EExcelTablePaginationType.DETAIL}
          onPageChange={onPageChange}
          onPageSizeChange={onShowSizeChange}
          pageSize={pageSize}
          currentPage={currentPage}
          totalRows={total || 0}
          tableContainerClassName="h-[28rem]"
          tableClassName="rounded-t-lg"
          className="mb-2"
          includeIdRow={true}
          pageSizeOptions={[10, 20, 50, 100]}
          loading={isLoading}
          onRowClicked={handleRowClick}
        />
      </FormSection>
    </div>
  );
};

export default ClaimsHistory;
